import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { ProductType } from '@/components/products/ProductsPromptModal/redux/types';
import { getProductPairQueryOptions, getProductsQueryOptions } from '@/queries/products';
import { GetProductsResponse } from '@/queries/products/types';
import { useSelector } from '@/redux/helper';

import { entitlementOf } from '../core/entitlement';
import speedyFlowSlice from '../redux/slice';

const { setSpeedyFlowProductPair } = speedyFlowSlice.actions;

/**
 * Fetch the pair of cards for the open flow, and hand it to the flow.
 *
 * The API chooses it (`GET /v2/products/pair`) from the link's `ppt`, the habitat, and the visitor —
 * their holdings, lapsed ones included. The answer also carries
 * each card's products, filed here under the query the card itself makes, so opening the flow is
 * one request rather than one per card. It asks once per flow and never again for the same visitor
 * and habitat: the query is keyed on who is asking and what they hold, so a purchase or a sign-in
 * asks afresh by itself.
 */
const useProductPairSync = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { visible, ppt, gate } = useSelector((state) => state.modals.speedyFlow);
  const visitor = useSelector((state) => {
    const { user } = state;
    const held = (user.userProducts ?? []).map((holding) => `${holding.key}@${holding.expiry}@${holding.scopeId ?? ''}`);
    return [user.logged ? user.userId : 'guest', user.persona ?? '', user.role ?? '', ...held].join('|');
  });
  // Until the session is read, `logged` is false for everybody, so the key above says guest while the
  // request still carries the session. A failed read after a successful one keeps the user, and with
  // it a key that is still right.
  const visitorKnown = useSelector(({ user }) => user.sessionChecked && (user.logged || !user.sessionError));
  const visitorFailed = useSelector(({ user }) => user.sessionError && !user.logged);

  const { data, isError } = useQuery({
    ...getProductPairQueryOptions({ ppt, habitatId: gate?.habitatId, entitlement: entitlementOf(gate) }, visitor),
    enabled: visible && !!gate && visitorKnown,
  });

  useEffect(() => {
    if (!visible) return;
    if (visitorFailed || (isError && !data)) {
      // Not left spinning: the plain default — the day passes — is what a signed-out visitor with
      // no history is offered, and each card still asks the API whether this visitor may buy it.
      const habitatId = gate?.habitatId;
      dispatch(
        setSpeedyFlowProductPair({
          habitatId,
          primaryProductType: 'all-habitats-day-pass',
          secondaryProductType: habitatId ? 'single-habitat-day-pass' : undefined,
        }),
      );
      return;
    }
    // A disabled query still hands back what is cached under its key, which is the guest's.
    if (!visitorKnown || !data) return;
    // Before the pair: the cards mount when it arrives, and should find their products waiting.
    data.products?.forEach(({ params, list }) =>
      queryClient.setQueryData<GetProductsResponse>(getProductsQueryOptions(params).queryKey as QueryKey, { list }),
    );
    dispatch(
      setSpeedyFlowProductPair({
        habitatId: data.habitatId,
        primaryProductType: data.primaryProductType as ProductType | undefined,
        secondaryProductType: data.secondaryProductType as ProductType | undefined,
        forEducator: data.forEducator,
      }),
    );
  }, [data, dispatch, gate?.habitatId, isError, queryClient, visible, visitorKnown, visitorFailed]);
};

export default useProductPairSync;
